import React, { useState } from 'react';
import { crearCategoria } from '../services/categoriasService';
import { useCategorias } from '../context/CategoriasContext';
import { SelectorEmoji, sugerirEmojis } from './GestionCategorias';
import { mensajeAmigable } from '../utils/errores';
import { useToast } from '../context/ToastContext';

// Mini-formulario para crear una categoría de PRODUCTO sin salir del
// formulario del producto. El prefijo genera los códigos (ej. POL → POL-0001).

const P = 'var(--color-primario,#DB2777)';
const input = { width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };

const sinTildes = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '');

// Prefijo sugerido a partir del nombre, sin repetir uno que ya exista:
// "Pollo" → POL, "Ropa Dama" → RD, "Huevos y lácteos" → HL.
export function sugerirPrefijo(nombre, usados) {
  const palabras = sinTildes(nombre).toUpperCase().replace(/[^A-Z ]/g, ' ')
    .split(/\s+/).filter((w) => w.length > 1 && !['DE', 'DEL', 'LA', 'LAS', 'LOS', 'EL', 'Y'].includes(w));
  if (palabras.length === 0) return '';
  const base = palabras.length > 1 ? palabras.slice(0, 3).map((w) => w[0]).join('') : palabras[0].slice(0, 3);
  const ocupado = (p) => usados.has(p);
  if (!ocupado(base)) return base;
  const letras = palabras.join('');
  for (let i = 0; i < letras.length; i++) {
    const p = (base.slice(0, 2) + letras[i]).slice(0, 4);
    if (!ocupado(p)) return p;
  }
  return '';
}

export default function NuevaCategoriaProductoInline({ onCreada, onCerrar }) {
  const { categorias, recargar } = useCategorias();
  const { mostrarToast } = useToast();
  const usados = new Set(categorias.map((c) => (c.prefijo || c.prefijo_codigo || '').toUpperCase()));
  const [nombre, setNombre] = useState('');
  const [prefijo, setPrefijo] = useState('');
  const [prefijoTocado, setPrefijoTocado] = useState(false);
  const [icono, setIcono] = useState('');
  const [guardando, setGuardando] = useState(false);

  const cambiarNombre = (v) => {
    setNombre(v);
    if (!prefijoTocado) setPrefijo(sugerirPrefijo(v, usados));
    if (!icono) { const s = sugerirEmojis(v); if (s.length) setIcono(s[0]); }
  };

  const guardar = async () => {
    const n = nombre.trim();
    if (n.length < 2) { mostrarToast('Escribe el nombre de la categoría', 'error'); return; }
    if (categorias.some((c) => sinTildes(c.nombre).toLowerCase() === sinTildes(n).toLowerCase())) {
      mostrarToast(`Ya tienes la categoría "${n}"`, 'error'); return;
    }
    const pre = prefijo.trim().toUpperCase();
    if (!/^[A-Z]{2,4}$/.test(pre)) { mostrarToast('El prefijo debe tener 2 a 4 letras (ej. POL)', 'error'); return; }
    if (usados.has(pre)) { mostrarToast(`El prefijo ${pre} ya lo usa otra categoría`, 'error'); return; }
    setGuardando(true);
    try {
      const orden = Math.max(0, ...categorias.map((c) => c.orden || 0)) + 1;
      await crearCategoria({ nombre: n, prefijo: pre, icono: icono || '📦', orden });
      await recargar();
      mostrarToast(`Categoría "${n}" creada ✅`, 'exito');
      onCreada(n);
    } catch (e) {
      const msg = (e?.message || '').includes('duplicate')
        ? 'Ya existe una categoría con ese nombre o prefijo.'
        : mensajeAmigable(e);
      mostrarToast(msg, 'error');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1400, padding: '16px' }} onClick={onCerrar}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px', padding: '22px', width: '380px', maxWidth: '100%', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h3 style={{ margin: 0, color: P, fontSize: '17px' }}>Nueva categoría</h3>
          <button onClick={onCerrar} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#9CA3AF' }}>✕</button>
        </div>

        <label htmlFor="nueva-cat-nombre" style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Nombre</label>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          <SelectorEmoji value={icono} onChange={setIcono} disabled={guardando} nombre={nombre} />
          <input id="nueva-cat-nombre" autoFocus value={nombre} maxLength={40} disabled={guardando}
            onChange={(e) => cambiarNombre(e.target.value)} placeholder="Ej. Pollo, Embutidos, Huevos" style={input} />
        </div>

        <label htmlFor="nueva-cat-prefijo" style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Prefijo del código</label>
        <input id="nueva-cat-prefijo" value={prefijo} maxLength={4} disabled={guardando}
          onChange={(e) => { setPrefijoTocado(true); setPrefijo(e.target.value.replace(/[^A-Za-z]/g, '').toUpperCase()); }}
          placeholder="Ej. POL" style={{ ...input, textTransform: 'uppercase' }} />
        <p style={{ margin: '4px 0 16px', fontSize: '12px', color: '#6B7280' }}>
          2 a 4 letras. Los productos se numeran así: <strong>{(prefijo || 'POL').toUpperCase()}-0001</strong>
        </p>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button type="button" onClick={onCerrar} disabled={guardando}
            style={{ flex: 1, padding: '11px', borderRadius: '10px', border: '1px solid #D1D5DB', background: 'white', fontWeight: 600, cursor: 'pointer' }}>
            Cancelar
          </button>
          <button type="button" onClick={guardar} disabled={guardando}
            style={{ flex: 2, padding: '11px', borderRadius: '10px', border: 'none', background: P, color: 'white', fontWeight: 700, cursor: guardando ? 'wait' : 'pointer' }}>
            {guardando ? 'Creando…' : 'Crear y usar'}
          </button>
        </div>
      </div>
    </div>
  );
}
